// /events/* — client event ingest + consent recording. docs/replan-v2.md R8
// (stubbed per CLAUDE.md §37.1). Event names validate against the taxonomy in
// @fajrarabi/analytics so the events table never accumulates unknown names.

import { EVENT_NAMES } from '@fajrarabi/analytics';
import { z } from 'zod';

export const eventNameSchema = z.enum(EVENT_NAMES);
export type EventNameInput = z.infer<typeof eventNameSchema>;

// Browser surfaces only — 'api' is reserved for server-emitted events.
export const clientEventSourceSchema = z.enum(['web', 'app', 'admin']);

const anonymousIdSchema = z
  .string()
  .min(8)
  .max(60)
  .regex(/^[A-Za-z0-9_-]+$/, 'anonymous id must be url-safe');

export const clientEventSchema = z.object({
  name: eventNameSchema,
  // Flat-ish payload; hard cap keeps a hostile client from bloating rows.
  properties: z.record(z.string().max(60), z.unknown()).optional().default({}),
  /** ISO timestamp of when the event happened client-side. */
  occurredAt: z.iso.datetime().optional(),
});

export const clientEventsInput = z.object({
  source: clientEventSourceSchema,
  anonymousId: anonymousIdSchema.optional(),
  events: z.array(clientEventSchema).min(1).max(20),
});
export type ClientEventsInput = z.infer<typeof clientEventsInput>;

export const consentChoicesSchema = z.object({
  analytics: z.boolean(),
  marketing: z.boolean(),
  perVendor: z
    .object({
      meta: z.boolean().optional(),
      google: z.boolean().optional(),
      tiktok: z.boolean().optional(),
      snap: z.boolean().optional(),
    })
    .optional(),
});
export type ConsentChoicesInput = z.infer<typeof consentChoicesSchema>;

export const consentInput = z.object({
  policyVersion: z.string().min(1).max(20),
  choices: consentChoicesSchema,
  anonymousId: anonymousIdSchema.optional(),
});
export type ConsentInput = z.infer<typeof consentInput>;

export const consentPolicyDto = z.object({
  version: z.string(),
  bodyKey: z.string(),
  effectiveFrom: z.string(),
});
export type ConsentPolicyDto = z.infer<typeof consentPolicyDto>;
